import { PRESENTING_PROBLEMS } from "../../../shared/domain/presentingProblems";
import { THERAPY_TYPES } from "../../../shared/domain/therapyTypes";

/** What a meditation is made of. Derived server-side from what it holds. */
export enum MeditationKind {
    TEXT = "text",
    AUDIO = "audio",
    TEXT_AND_AUDIO = "textAndAudio",
}

export enum MeditationVisibility {
    PRIVATE = "private",
    PUBLIC = "public",
}

/**
 * The terms a therapist publishes under.
 *
 * Publishing is not surrendering authorship, and the fear of the second is
 * what stops people doing the first — so the default reserves every right
 * beyond using the meditation inside Alchely.
 */
export enum MeditationLicense {
    ALL_RIGHTS_RESERVED = "allRightsReserved",
    CC_BY = "ccBy",
    CC_BY_NC = "ccByNc",
    CC_BY_SA = "ccBySa",
    CC0 = "cc0",
}

/** How many meditations a page of a library shows. */
export const MEDITATIONS_PAGE_SIZE = 10;

/** How many tracks a page of a music shelf shows. */
export const TRACKS_PAGE_SIZE = 10;

export const MEDITATION_LICENSES: MeditationLicense[] =
    Object.values(MeditationLicense);

/**
 * The royalty-free beds. Values mirror
 * `domain.models.meditation.BackgroundTrack` on the API.
 */
export enum BackgroundTrackId {
    NONE = "none",
    /**
     * The three we made are no longer offered — the therapist's own library
     * covers what they were for — but they stay here so a meditation saved
     * with one still names, and plays, the bed it was made with.
     */
    OCEAN = "ocean",
    FOREST = "forest",
    SINGING_BOWLS = "singingBowls",
}

export type TherapyType = (typeof THERAPY_TYPES)[number];
export type PresentingProblem = (typeof PRESENTING_PROBLEMS)[number];

export interface Meditation {
    id: string;
    title: string;
    script?: string | null;
    /** The object name never leaves the API; this is all the client needs. */
    has_audio: boolean;
    audio_duration_seconds?: number | null;
    background_track: BackgroundTrackId;
    /** Set when the bed is one of the therapist's own tracks. */
    background_track_id?: string | null;
    has_custom_background?: boolean;
    /** The mix the therapist approved: 0..1 each. */
    voice_volume?: number;
    background_volume?: number;
    reading_speed_wpm: number;
    description?: string | null;
    therapy_types: TherapyType[];
    /** The therapist's own words for how they work, shown verbatim. */
    custom_therapy_types: string[];
    presenting_problems: PresentingProblem[];
    custom_presenting_problems: string[];
    visibility: MeditationVisibility;
    license: MeditationLicense;
    author_name?: string | null;
    /** Sent only where the author's public profile shows a photo. */
    author_photo_url?: string | null;
    kind: MeditationKind;
    /** The recording's length, or how long the script takes at its own pace. */
    estimated_seconds: number;
    /** What the author says it runs to, when they said. */
    declared_duration_seconds?: number | null;
    /** A BCP 47 code. Null on the ones written before the field existed. */
    language?: string | null;
    /** Whether the caller may edit it: author, or a centre administrator. */
    is_owner: boolean;
    /** In the library of the caller's centre rather than anybody's own. */
    in_center_library?: boolean;
    published_at?: string | null;
    source_meditation_id?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    /** How many therapists liked it, and whether the caller is one. */
    like_count?: number;
    liked_by_me?: boolean;
    /** The caller's own copy of it, when they already added it to their library. */
    my_copy_id?: string | null;
    /** How many times it was added to somebody's own library. */
    copy_count?: number;
}

/** A heart after a like or an unlike, as the API answers it. */
export interface MeditationLikeState {
    like_count: number;
    liked_by_me: boolean;
}

export interface MeditationFilters {
    kind?: MeditationKind;
    therapyType?: TherapyType;
    presentingProblem?: PresentingProblem;
    language?: string;
    search?: string;
    /** Part of the author's name, as the catalogue shows it. */
    author?: string;
    /** How the public catalogue is sorted; newest first when unset. */
    order?: "recent" | "likes";
}

export interface MeditationDraft {
    title: string;
    script?: string | null;
    audio_path?: string | null;
    audio_duration_seconds?: number | null;
    background_track?: BackgroundTrackId;
    background_track_id?: string | null;
    voice_volume?: number;
    background_volume?: number;
    declared_duration_seconds?: number | null;
    language?: string | null;
    reading_speed_wpm?: number;
    description?: string | null;
    therapy_types?: TherapyType[];
    custom_therapy_types?: string[];
    presenting_problems?: PresentingProblem[];
    custom_presenting_problems?: string[];
    license?: MeditationLicense;
    author_name?: string | null;
    /** Into the centre's shared library. Administrators only. */
    for_center?: boolean;
}

/** One piece of music in the therapist's own library. */
export interface MeditationTrack {
    id: string;
    name: string;
    duration_seconds?: number | null;
    created_at?: string | null;
    /** On the caller's centre shelf rather than their own. */
    in_center_library?: boolean;
    /** Whether the caller may rename or remove it. */
    can_manage?: boolean;
    /** In the public catalogue, for any therapist to use. */
    is_public?: boolean;
    /** The name it is shared under; a copy keeps it, to credit who shared it. */
    author_name?: string | null;
    /** How many therapists liked it, and whether the caller is one. */
    like_count?: number;
    liked_by_me?: boolean;
    /** The caller's own copy of it, when they already added it to their shelf. */
    my_copy_id?: string | null;
    /** How many times it was added to somebody's own shelf. */
    copy_count?: number;
    /** How many of the caller's own meditations play it. Own shelf only. */
    meditation_count?: number;
    /** Added from someone else's share: theirs to share, not the caller's. */
    is_copy?: boolean;
}

/** Narrowing the public catalogue of music. */
export interface MeditationTrackFilters {
    /** Part of the track's name. */
    search?: string;
    /** Part of the name it was shared under. */
    author?: string;
    /** Newest first unless "likes". */
    order?: "recent" | "likes";
}

export interface MeditationTrackUpload {
    upload_url: string;
    audio_path: string;
}

export interface MeditationPlayback {
    playback_url: string;
    expires_in_seconds: number;
    /** Sent when the bed is the therapist's own file rather than one of ours. */
    background_playback_url?: string | null;
}

export interface MeditationAudioUpload {
    upload_url: string;
    audio_path: string;
}

/** A patient who has this meditation in their own portal. */
export interface MeditationRecipient {
    patient_id: string;
    name: string;
}

/**
 * How many tags a meditation may carry, catalogue and custom counted together.
 *
 * Mirrors `MAX_THERAPY_TYPES` in `domain.models.meditation`: past a dozen the
 * tags stop saying where a meditation belongs and start listing everywhere it
 * could conceivably go.
 */
export const MAX_THERAPY_TYPES = 12;

/** The same cap, for what a meditation is for. Mirrors the API's. */
export const MAX_PRESENTING_PROBLEMS = 12;

/** `mm:ss`, which is how a therapist reads a meditation's length. */
export function formatDuration(totalSeconds: number): string {
    const safe = Math.max(0, Math.round(totalSeconds));
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Whether a draft is complete enough to save.
 *
 * The same rule the API enforces, checked here so the therapist finds out
 * before they lose the form rather than after.
 */
export function isDraftSaveable(draft: {
    title: string;
    script?: string | null;
    audioPath?: string | null;
}): boolean {
    if (!draft.title.trim()) return false;
    return Boolean(draft.script?.trim() || draft.audioPath);
}
